// Vue
import { computed, ref, watch } from "vue";

// Data Providers
import { useTransportInventory, TransportInventoryType } from "@/dataProviders/TransportInventory";
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { DashboardSummaryResponseType } from "@/features/Dashboard";
import { useTransportTypeOptions } from "@/dataProviders/TransportTypeOptions";
import { useIndicators } from "@/dataProviders/Indicators";

// Composables
import { useSelectedIndicatorMapping } from "@/composables/useSelectedIndicatorMapping";

// Stores
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { modelFilterForRequest, modelFilterRequestKey } from "@/stores/dashboard/selectedModelsFilter";

// Dependencies
import { defineStore } from "pinia";

// Types
import {
  TransportMappingListInventoryType,
  TransportMappingListInventoryElementType,
} from "../types/TransportMappingListInventoryType";

// API
import { getDashboardSummary } from "@/api/services/Dashboard";

// GraphQL
import {
  DeleteProjectTransportMappingWithVersionDocument,
  type DeleteProjectTransportMappingWithVersionMutation,
  type DeleteProjectTransportMappingWithVersionMutationVariables,
  GetProjectTransportMappingQuery,
  GetProjectTransportMappingQueryVariables,
  InsertProjectVersionDocument,
  type InsertProjectVersionMutation,
  type InsertProjectVersionMutationVariables,
  useGetProjectTransportMappingQuery,
  useInsertProjectTransportMappingMutation,
  useDeleteProjectTransportMappingMutation,
  InsertProjectTransportMappingMutationVariables,
  ProjectTransportMappingEmissionFactor_Arr_Rel_Insert_Input,
  useDeleteProjectTransportMappingEmissionFactorsMutationMutation,
} from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import * as Sentry from "@sentry/vue";
import { isVersioningSchemaMissing } from "@/pages/ProjectVersionHistory/services/versioningSchema";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";
import useTransportTypesForRead from "../composables/useTransportTypesForRead.composable";
import { blockedFromProjectEditFor } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import calculateStandardValueEmissions from "../utils/calculateStandardValueEmissions";
import calculateTransportMappingTotal from "../utils/calculateTransportMappingTotal";
import { buildTransportVersionInput } from "../utils/transportVersionSnapshot";
import withApolloClient from "@/graphql/services/withApolloClient";

interface TransportMappingListProvider {
  getMappingInventory: () => TransportMappingListInventoryType;
  getTransportMapping: () => GetProjectTransportMappingQuery["project_transport_mapping"] | undefined;
  getForceToggleState: () => boolean;
  getMappedInventory: () => TransportMappingListInventoryType;

  // API Methods
  insertProjectTransportMapping: (data: {
    materialId?: string;
    distance: number;
    emissionFactors?: ProjectTransportMappingEmissionFactor_Arr_Rel_Insert_Input["data"];
    transportEmissionFactorEmission?: number;
    transportStandardValueId?: string;
    transportTypeId?: string;
  }) => Promise<void>;
  deleteProjectTransportMapping: (id: string, projectId: string) => Promise<void>;

  // Methods
  getTransportInventoryEmission: () => number;
  getTransportInventoryEmissionUnit: () => string;
  getTransportMappingPercentage: () => number;
  calculateTransportMaterialEmission: (material: TransportMappingListInventoryElementType) => number;
  calculateEpdBasedEmission: (material: TransportMappingListInventoryElementType, indicatorTypeId?: number | null) => number;
  mapInventoryWithTransportMappingEntries: () => void;
  forceToggle: () => void;

  // Utility
  getLoading: () => boolean;
  getInitialLoading: () => boolean;
  getLoadingUi: () => boolean;
  getError: () => boolean;
}

export type InsertProjectTransportMappingInput = {
  materialId?: string;
  distance?: number;
  emissionFactors?: ProjectTransportMappingEmissionFactor_Arr_Rel_Insert_Input["data"];
  transportEmissionFactorEmission?: number;
  transportStandardValueId?: string;
  transportTypeId?: string;
};

const useTransportMappingList = defineStore("TransportMappingListProvider", (): TransportMappingListProvider => {
  // Data Providers

  const projectWrapper = useProjectWrapper();
  const transportInventory = useTransportInventory();
  const transportTypeOptions = useTransportTypeOptions();
  const indicators = useIndicators();

  // Kept as an instance (not just the action): dashboardStore.models/selectedModels feed the
  // summary's Models scope below, alongside refreshDashboard.
  const dashboardStore = useDashboard();
  const { refreshDashboard } = dashboardStore;

  // Follow the Overview-selected unit (indicator-type-mapping) for the summary fetch
  const { selectedMappingId } = useSelectedIndicatorMapping(computed(() => projectWrapper.getProjectWrapper().project?.id));

  // Refs

  const revisionsStore = useProjectRevisionsStore();
  // Resolved from the same pin `queryVariables` filters the mapping rows on, so an entry and the
  // type it names always describe one revision.
  const transportTypesForRead = useTransportTypesForRead();

  const mappingInventory = ref<TransportMappingListInventoryType>([]);
  const transportMapping = ref<GetProjectTransportMappingQuery["project_transport_mapping"]>();
  const mappedInventory = ref<TransportMappingListInventoryType>([]);
  const queryProjectId = ref<string>("");
  // Reactive: the revision filter rides along, so a preview toggle refetches on its own. Filtering
  // on frozenAt (not an id) when unpinned means it's correct before revisions have loaded.
  const queryVariables = computed<GetProjectTransportMappingQueryVariables>(() => {
    const pin = queryProjectId.value ? revisionsStore.readPinRevisionIdFor(queryProjectId.value) : null;
    return {
      project_id: queryProjectId.value,
      materialIdIsNull: false,
      revisionFilter: pin ? { revisionId: { _eq: pin } } : { revision: { frozenAt: { _is_null: true } } },
    };
  });
  const queryEnabled = ref<boolean>(false);
  const summary = ref<DashboardSummaryResponseType>();
  const forceToggleState = ref<boolean>(false);
  // Tracks the FIRST successful fetch only, so a post-write refetch (add/delete) doesn't
  // re-trigger the page-level loading spinner the way the raw query loading flag does.
  const hasLoadedTransportMappingOnce = ref<boolean>(false);

  // Queries

  // Delete project transport mapping
  const {
    mutate: deleteProjectTransportMappingMutation,
    loading: deleteProjectTransportMappingLoading,
    error: deleteProjectTransportMappingError,
  } = withApolloClient(() => useDeleteProjectTransportMappingMutation());

  // Delete project transport mapping emission factors
  const {
    mutate: deleteProjectTransportMappingEmissionFactorsMutation,
    loading: deleteProjectTransportMappingEmissionFactorsLoading,
  } = withApolloClient(() => useDeleteProjectTransportMappingEmissionFactorsMutationMutation());

  // Insert project transport mapping
  const {
    mutate: insertProjectTransportMappingMutation,
    loading: insertProjectTransportMappingLoading,
    error: insertProjectTransportMappingError,
  } = withApolloClient(() => useInsertProjectTransportMappingMutation());

  // Get the project transport mapping
  const {
    result: queryProjectTransportMappingResult,
    loading: queryProjectTransportMappingLoading,
    error: queryProjectTransportMappingError,
    refetch: refetchProjectTransportMapping,
  } = withApolloClient(() =>
    useGetProjectTransportMappingQuery(queryVariables, {
      enabled: queryEnabled,
      fetchPolicy: "no-cache",
    })
  );

  // Getters

  function getMappingInventory() {
    return mappingInventory.value;
  }

  function getTransportMapping() {
    return transportMapping.value;
  }

  function getMappedInventory() {
    return mappedInventory.value;
  }

  function getForceToggleState() {
    return forceToggleState.value;
  }

  // Setters

  function setMappingInventory(newInventory: TransportMappingListInventoryType) {
    mappingInventory.value = newInventory;
  }

  function setTransportMapping(newTransportMapping: GetProjectTransportMappingQuery["project_transport_mapping"]) {
    transportMapping.value = newTransportMapping;
  }

  function setMappedInventory(newInventory: TransportMappingListInventoryType) {
    mappedInventory.value = newInventory;
  }

  function setForceToggleState(value: boolean) {
    forceToggleState.value = value;
  }

  function setQueryVariables(newProject: string) {
    queryProjectId.value = newProject;
  }

  function setQueryEnabled(value: boolean) {
    queryEnabled.value = value;
  }

  // Versioning

  function materialNameFor(materialId?: string | null): string | null {
    if (!materialId) return null;
    return getMappingInventory().find((item) => item.material.id === materialId)?.material.name ?? null;
  }

  // Best-effort insert-then-version: the row id is server-generated, so it can't bundle with the
  // write. A failure never blocks the save — the trail is just a display-only log.
  async function writeTransportVersionRow(version: ReturnType<typeof buildTransportVersionInput>) {
    try {
      await gaiupApolloClient.mutate<InsertProjectVersionMutation, InsertProjectVersionMutationVariables>({
        mutation: InsertProjectVersionDocument,
        variables: { version },
        fetchPolicy: "no-cache",
      });
    } catch (mutationError) {
      // Schema not migrated here yet is expected (rollout: Hasura → API → UI); anything else is
      // a real (but non-blocking) trail gap worth knowing about.
      if (!isVersioningSchemaMissing(mutationError)) Sentry.captureException(mutationError);
    }
  }

  // API Methods

  async function deleteProjectTransportMapping(id: string, projectId: string) {
    // Last line of defence: without it, a write while previewing would misfile onto the CURRENT
    // revision (trigger-stamped), and a Viewer's write would surface as an unhandled rejection.
    if (blockedFromProjectEditFor(projectId)) return;

    const version = buildTransportVersionInput({
      projectId,
      operation: "delete",
      summary: "Transport mapping removed",
      entityId: id,
      entityLabel: materialNameFor(transportMapping.value?.find((entry) => entry.id === id)?.material_id),
    });

    // The delete travels with its trail row in one document (one transaction); environments
    // without the versioning schema fall back to the plain two-step delete.
    let deleted: boolean;
    try {
      const result = await gaiupApolloClient.mutate<
        DeleteProjectTransportMappingWithVersionMutation,
        DeleteProjectTransportMappingWithVersionMutationVariables
      >({
        mutation: DeleteProjectTransportMappingWithVersionDocument,
        variables: { id, projectId, version },
        fetchPolicy: "no-cache",
      });
      deleted = !!result?.data;
    } catch (mutationError) {
      if (!isVersioningSchemaMissing(mutationError)) throw mutationError;
      await deleteProjectTransportMappingEmissionFactorsMutation({
        id,
        projectId,
      });
      const result = await deleteProjectTransportMappingMutation({
        id,
        projectId,
      });
      deleted = !!result?.data;
    }

    if (!deleted) {
      return;
    }

    // Re-read the working state (the row set the mutation changed).
    await refetchProjectTransportMapping();

    // Update summary on change
    await updateSummary();

    // Reflect the change on the dashboard/BCI without a page reload
    await refreshDashboard();
  }

  async function insertProjectTransportMapping({
    emissionFactors,
    materialId,
    distance,
    transportEmissionFactorEmission,
    transportStandardValueId,
    transportTypeId,
  }: InsertProjectTransportMappingInput) {
    const projectId = projectWrapper.getProjectWrapper().project.id;

    if (blockedFromProjectEditFor(projectId)) return;

    const mappingData: InsertProjectTransportMappingMutationVariables = {
      project_id: projectId,
      material_id: materialId,
      distance,
      project_transport_id: transportTypeId,
      emissionFactors: emissionFactors ? { data: emissionFactors } : undefined,
      transportEmissionFactorEmission,
      transportStandardValueId,
    };

    const result = await insertProjectTransportMappingMutation(mappingData);

    if (!result?.data?.insert_project_transport_mapping_one) {
      return;
    }

    // Re-read the working state (the row set the mutation changed).
    const insertedMapping = result.data.insert_project_transport_mapping_one;
    await refetchProjectTransportMapping();

    await writeTransportVersionRow(
      buildTransportVersionInput({
        projectId,
        operation: "create",
        summary: "Transport mapping added",
        entityId: insertedMapping.id,
        entityLabel: materialNameFor(materialId),
      })
    );

    // Update summary on change
    await updateSummary();

    // Reflect the change on the dashboard/BCI without a page reload
    await refreshDashboard();
  }

  // Methods

  async function updateSummary() {
    const projectId = projectWrapper.getProjectWrapper().project.id;
    const newSummary = await getDashboardSummary(projectId, {
      indicatorTypeMappingId: selectedMappingId.value ?? undefined,
      // Feeds the toolbar's % MAPPED bar, so it must describe the same revision as the rows below.
      revisionId: revisionsStore.readPinRevisionIdFor(projectId) ?? undefined,
      // Same for the Models selection: read live at call time, since a stale value can name a
      // model the project no longer has, which the backend rejects with a 400.
      models: modelFilterForRequest(dashboardStore.models, dashboardStore.selectedModels),
    });
    summary.value = newSummary;
  }

  // Refresh the summary (unit + value) when the Overview unit selection changes
  watch(selectedMappingId, () => {
    void updateSummary();
  });

  function mapInventoryToMappingInventory(inventory: TransportInventoryType): TransportMappingListInventoryType {
    // Convert the inventory to TransportMappingListInventoryType
    const mappingInventory: TransportMappingListInventoryType = inventory.map((item) => ({
      weight: item.weight,
      material: item.material,
      transportBuildingMaterial: item.transportBuildingMaterial,
      mappingEntries: [],
    }));

    return mappingInventory;
  }

  function mapInventoryWithTransportMappingEntries() {
    const mappingInventory = getMappingInventory();
    const transportMapping = getTransportMapping();

    // Exit if no transport mapping
    if (!transportMapping) {
      return mappingInventory;
    }

    const mappedInventory: TransportMappingListInventoryType = [];

    // Map the inventory with the mapping entries based on the material id into mappedInventory
    for (const item of mappingInventory) {
      // Find the transport mapping entries for the material
      const mappings = transportMapping.filter((mapping) => mapping.material_id === item.material.id);

      // Add the mapping entries to the inventory item
      item.mappingEntries = mappings.map((mapping) => ({
        id: mapping.id,
        distance: mapping.distance,
        transportEmissionFactors: mapping.emissionFactors,
        transportEmissionFactorEmission: mapping.transportEmissionFactorEmission,
        transportStandardValue: mapping.transportStandardValue,
        transportTypeId: mapping.project_transport_id,
      }));

      mappedInventory.push(item);
    }

    // Sort the inventory by count size
    mappedInventory.sort((a, b) => b.transportBuildingMaterial.length - a.transportBuildingMaterial.length);

    setMappedInventory(mappedInventory);
  }

  function getTransportMappingPercentage(): number {
    return summary.value?.mapping?.transport?.percentage ?? 0;
  }

  function getTransportInventoryEmission(): number {
    return summary.value?.mapping?.transport?.value ?? 0;
  }

  function getTransportInventoryEmissionUnit(): string {
    return summary.value?.unit?.label ?? "";
  }

  function calculateTransportMaterialEmission(material: TransportMappingListInventoryElementType): number {
    const entriesEmission = material.mappingEntries.reduce((acc, entry) => {
      if (entry.transportStandardValue) {
        return acc + calculateStandardValueEmissions(entry.transportStandardValue.emission, material.weight, 0, 0).total;
      }

      if (entry.transportEmissionFactorEmission && entry.transportEmissionFactors.length > 0) {
        return acc + calculateTransportMappingTotal(entry, material.weight);
      }

      // Must resolve from the pinned revision's own copy, not the working rows — see
      // versioning.md's transport-types caveat for why.
      const transportType = transportTypesForRead.transportTypeById(entry.transportTypeId);

      // Exit if no transport type
      if (!transportType) {
        return acc;
      }

      // Get the emission for the transport type
      const emission = transportTypeOptions.getEmissionFromTransportType(transportType);

      // Calculate the emission for the entry
      return acc + emission * entry.distance * (material.weight / 1000);
    }, 0);

    return entriesEmission;
  }

  function calculateEpdBasedEmission(
    material: TransportMappingListInventoryElementType,
    indicatorTypeId?: number | null
  ): number {
    // Get the indicator type identifier
    const indicatorIdentifier = indicators.getIdentifierFromIndicatorTypeId(indicatorTypeId ?? 0);

    // Accumulate the total EPD emission for the material
    const totalEpdEmission = material.transportBuildingMaterial.reduce((acc, item) => {
      return acc + (item?.emission[indicatorIdentifier as keyof typeof item.emission] ?? 0);
    }, 0);

    return totalEpdEmission;
  }

  function forceToggle() {
    setForceToggleState(!forceToggleState.value);
  }

  // Watchers

  // Set the query variables when the project changes
  watch(
    () => projectWrapper.getProjectWrapper().project.id,
    (newId) => {
      // Reset if no project
      if (!newId) {
        setQueryVariables("");
        setQueryEnabled(false);
        hasLoadedTransportMappingOnce.value = false;
        return;
      }

      // Set the query variables now we have the project
      setQueryVariables(newId);
      setQueryEnabled(true);
      // Reset: this store outlives a project switch, so without this a switch inherits the
      // previous project's "already loaded" state and skips the initial-load spinner.
      hasLoadedTransportMappingOnce.value = false;
    },
    { immediate: true }
  );

  // The query result IS the rendered row set — its variables already carry the revision filter,
  // so previews and the working state arrive through the same path.
  watch(queryProjectTransportMappingResult, async (newValue) => {
    if (newValue) {
      setTransportMapping(newValue.project_transport_mapping);
      hasLoadedTransportMappingOnce.value = true;

      // Update summary on change
      await updateSummary();
    }
  });

  // A switch moves the pointer while the pin stays null on both sides, so the query variables
  // never change on their own — re-read the working state from the pointer directly.
  watch(
    () => revisionsStore.activeMetaFor(projectWrapper.getProjectWrapper().project?.id ?? "")?.id,
    async (next, previous) => {
      if (!next || !previous || next === previous) return;
      await refetchProjectTransportMapping();
    }
  );

  // Its own two-part key: it can't ride on the Apollo watcher above, which won't fire when two
  // revisions hold the same (or no) mapping rows — exactly when a stale figure is hardest to spot.
  watch(
    () => {
      const projectId = projectWrapper.getProjectWrapper().project?.id ?? "";
      return projectId ? `${revisionsStore.readPinRevisionIdFor(projectId)}|${revisionsStore.activeMetaFor(projectId)?.id}` : "";
    },
    () => {
      if (projectWrapper.getProjectWrapper().project?.id) void updateSummary();
    }
  );

  // Same for the Models selection: keyed on the request it produces, so the dashboard's initial
  // seed-all (which matches the first summary call) doesn't double the request on the wire.
  watch(
    () => modelFilterRequestKey(dashboardStore.models, dashboardStore.selectedModels),
    () => {
      if (projectWrapper.getProjectWrapper().project?.id) void updateSummary();
    }
  );

  watch(
    transportInventory.getInventory,
    (newInventory) => {
      setMappingInventory(mapInventoryToMappingInventory(newInventory));
    },
    { deep: true }
  );

  // Utility

  const loadingUi = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const error = ref<boolean>(false);

  function getError() {
    return error.value;
  }

  function getLoading() {
    return loading.value;
  }

  // True only for the FIRST fetch — a post-write refetch also flips `loading`, but callers that
  // gate a full unmount on it should not treat a revalidation like a fresh load.
  function getInitialLoading() {
    return loading.value && !hasLoadedTransportMappingOnce.value;
  }

  function getLoadingUi() {
    return loadingUi.value;
  }

  watch([queryProjectTransportMappingLoading], ([newLoading1]) => {
    loading.value = newLoading1;
  });

  watch(
    [
      insertProjectTransportMappingLoading,
      deleteProjectTransportMappingLoading,
      deleteProjectTransportMappingEmissionFactorsLoading,
    ],
    (values) => {
      loadingUi.value = values.some((loadingState) => loadingState);
    }
  );

  watch(
    [queryProjectTransportMappingError, insertProjectTransportMappingError, deleteProjectTransportMappingError],
    ([newQueryProjectTransportMappingError, newInsertProjectTransportMappingError, newDeleteProjectTransportMappingError]) => {
      console.error({
        newQueryProjectTransportMappingError,
        newInsertProjectTransportMappingError,
        newDeleteProjectTransportMappingError,
      });
      error.value = true;
    }
  );

  // Returns

  return {
    getMappingInventory,
    getForceToggleState,
    getMappedInventory,
    getTransportMapping,

    // API Methods
    insertProjectTransportMapping,
    deleteProjectTransportMapping,

    // Methods
    getTransportInventoryEmission,
    getTransportInventoryEmissionUnit,
    getTransportMappingPercentage,
    calculateTransportMaterialEmission,
    calculateEpdBasedEmission,
    mapInventoryWithTransportMappingEntries,
    forceToggle,

    // Utility
    getLoading,
    getInitialLoading,
    getLoadingUi,
    getError,
  };
});

export default useTransportMappingList;
